import { CookieManager } from "./cookie-manager"
import { FronteggService } from "./frontegg-service"
import { AppHolder, initialize } from '@frontegg/js';
import { ContextHolder } from '@frontegg/rest-api';
import jwt_decode from "jwt-decode";

class SessionManager {
  #defaultTimeout = 15;
  #warningMinsBeforeLogout = 2;
  constructor(options) {
    this.postLogoutRedirectURI = options.postLogoutRedirectURI;
    this.onLogout = options.onLogout || function () { };
    this.onLogout = options.onLogout || function () { };
    this.logoutTimeout = null;
    this.logoutWarningTimeout = null;
    this.app = null;
    this.token = null;
    this.sessionTimeout = this.#defaultTimeout;
    this.domain = options.domain || this.resolveSharedDomain();
    this.cookieManager = new CookieManager({ isDevelopment: options.isDevelopment, domain: this.domain });
    this.logger = options.logger || false;
    this.loginUrl = options.loginUrl;
  }

  fronteggInitialize(authUrl, clientId) {
    return initialize({
      contextOptions: {
        baseUrl: authUrl,
        clientId: clientId,
      },
      authOptions: {
        keepSessionAlive: true
      },
      hostedLoginBox: true
    });
  }

  run(options) {
    if (typeof options === "undefined") {
      options = {}
    }

    this.app = options.app;

    this.onAccessTokenChange = options.onAccessTokenChange || function () { };
    this.onExpirationWarning = options.onExpirationWarning || function () { };
    this.onRefreshExpiration = options.onRefreshExpiration || function () {};
    const onUnAuthenticated = options.onUnAuthenticated || function () { };

    this.checkSessionExpired()

    const onInit = this.onInit.bind(this);
    const onAuthenticated = this.onAuthenticated.bind(this);
    const subscribeToAuth = function(){
      const fronteggService = new FronteggService({ app: this.app });
      fronteggService.subscribeToAuth(onInit, onAuthenticated, onUnAuthenticated)
    }.bind(this);

    if (this.app) {
      subscribeToAuth();
    } else {
      this.findAppWhenInitiated(subscribeToAuth);
    }
  }

  findAppWhenInitiated(callback) {
    let app;
    let checkFronteggAppInitiated = setInterval(() => {
      app = this.retrieveApp();
      if (app) {
        this.app = app;
        callback();
        clearInterval(checkFronteggAppInitiated);
      }
    }, 3000);
  }

  retrieveApp() {
    let appHolder;
    if (typeof Frontegg !== "undefined") {
      appHolder = Frontegg.AppHolder
    } else {
      appHolder = AppHolder;
    }

    return appHolder._apps['default'];
  }

  onInit() {
    if (this.cookieManager.retrieve('session_logout')) {
      this.cookieManager.remove('session_logout');
    }
  }

  onAuthenticated(authData) {
    if (!this.token) {
      this.setupSessionWatchers();
    }
    if (this.token != authData.user.accessToken) {
      if (this.logger) {
        console.log('There is a new token');
      }
      this.token = authData.user.accessToken;
      this.onAccessTokenChange(this.token);
      this.retrieveSessionTimeout();
      this.checkForActiveSession();
    }
  }

  setupSessionWatchers() {
    setInterval(() => {
      if (this.cookieManager.retrieve('session_logout')) {
        if (this.logger) {
          console.log('Another app logged out');
        }
        this.logout()
      }
    }, 5000);

    // we will check on session expiration if the user had activity and warn it 2mins before
    this.scheduleLogout(this.expiresIn())
    this.scheduleLogoutWarning(this.warnExpirationIn())
    this.updateExpiresAt();
  }

  logout() {
    this.onLogout();
    if (!this.cookieManager.retrieve('session_logout')) {
      this.cookieManager.store('session_logout', true);
    }
    this.cookieManager.remove('session_expires_at')

    const baseUrl = this.loginUrl || ContextHolder.getContext().baseUrl;
    const url = new URL(`${baseUrl}/oauth/logout`);
    let options = { post_logout_redirect_uri: this.redirectToUri(this.postLogoutRedirectURI) }
    url.search = new URLSearchParams(options);
    window.location.href = url.toString();
  }

  redirectToUri(path) {
    return `${window.location.origin}${path}`;
  }

  scheduleLogout(expiresInSeconds) {
    clearTimeout(this.logoutTimeout);
    let checkSessionExpired = this.checkSessionExpired.bind(this);
    this.logoutTimeout = setTimeout(function () {
      checkSessionExpired()
    }, expiresInSeconds * 1000);
  }

  checkForActiveSession() {
    let active = function () {
      this.updateExpiresAt();
      this.scheduleLogout(this.expiresIn())
      this.scheduleLogoutWarning(this.warnExpirationIn())
    }
    const refreshFrequency = 60 * 1000;
    document.addEventListener('mousemove', this.throttle(active.bind(this), refreshFrequency));
  }

  scheduleLogoutWarning(expiresInSeconds) {
    clearTimeout(this.logoutWarningTimeout);
    let cookieManager = this.cookieManager;
    let onExpirationWarning = this.onExpirationWarning;
    let warningMinsBeforeLogout = this.#warningMinsBeforeLogout;
    this.logoutWarningTimeout = setTimeout(function () {
      const expiresAt = cookieManager.retrieve('session_expires_at');
      if (expiresAt && (new Date(Date.parse(expiresAt) - 1000 * 60 * warningMinsBeforeLogout) < new Date())) {
        onExpirationWarning()
      }
    }, expiresInSeconds * 1000);
  }

  calculateExpiresAt(sessionTimeout) {
    return new Date((new Date()).getTime() + sessionTimeout * 60 * 1000)
  }

  retrieveSessionTimeout() {
    let retrieveSessionTimeout = this.retrieveSessionTimeout.bind(this);

    const tenants = this.app.store.getState().auth.tenantsState.tenants;
    if (tenants.length === 0) {
      // this api call is async so we don't know when it will be loaded, so we try again in 3 seconds
      setTimeout(function () {
        retrieveSessionTimeout();
      }, 3000);
      return;
    }

    const token = this.token;
    this.setSessionTimeout(token, tenants);
  }

  setSessionTimeout(token, tenants) {
    try {
      const originalTimeout = this.sessionTimeout;
      const organization = tenants.find(function (element) {
        return element.tenantId === jwt_decode(token).tenantId
      })
      if (!organization) {
        if (this.logger) {
          console.error('Could not find organization');
        }
        return;
      }
      this.sessionTimeout = JSON.parse(organization.metadata).sessionTimeoutInMinutes;
      if (this.sessionTimeout !== originalTimeout) {
        this.updateExpiresAt();
        if (this.logger) {
          console.log('Session timeout is', this.sessionTimeout);
        }
      }
    } catch (e) {
      if (this.logger) {
        console.log('The token is ', token); // TODO: remove me if no ocurrences on May 2023
        console.error('Could not set session timeout ', e);
      }
    }
  }

  checkSessionExpired() {
    const expiresAt = this.cookieManager.retrieve('session_expires_at');
    if (expiresAt && (new Date(Date.parse(expiresAt)) < new Date())) {
      if (this.logger) {
        console.log('Session expired, logging out');
      }
      this.cookieManager.remove('session_expires_at')
      this.logout();
    }
  }

  throttle(func, timeFrame) {
    var lastTime = 0;
    return function () {
      var now = Date.now();
      if (now - lastTime >= timeFrame) {
        func();
        lastTime = now;
      }
    };
  }

  expiresIn() {
    return this.sessionTimeout * 60;
  }

  warnExpirationIn() {
    return (this.sessionTimeout - this.#warningMinsBeforeLogout) * 60;
  }

  updateExpiresAt() {
    if (this.logger) {
      console.log('Refreshing expires at');
    }

    this.cookieManager.store('session_expires_at', this.calculateExpiresAt(this.sessionTimeout));
    this.onRefreshExpiration();
  }

  resolveSharedDomain() {
    const url = window.location.hostname;
    const rootDomainRegex = /(\.[^.]+){2,}$/;

    return url.match(rootDomainRegex) ? url.match(rootDomainRegex)[0] : url; // should output ".yourdomain.com"
  }
}

export default SessionManager
